import type { ReactNode } from "react";
import { useEffect, useId, useRef } from "react";
import { useTranslation } from "react-i18next";

/**
 * The modal five of the nine screens are (.docs/08 §1).
 *
 * Built on the native `<dialog>` and `showModal()`, which is the whole reason
 * this file is short: the focus trap, Escape, the top layer and the inertness
 * of everything behind are the browser's, not ours. Every hand-rolled modal
 * eventually gets one of those wrong, and the one that gets it wrong here is
 * the one a seller is typing a client's consent into.
 *
 * Two things `showModal()` does *not* do, which are handled below: it does not
 * stop the page behind from scrolling on iOS, and its Escape closes the element
 * without telling React, leaving `open` true and the dialog shut.
 */
export function Dialog({
  open,
  onClose,
  title,
  kicker,
  width = 820,
  children,
  footer,
  closeOnBackdrop = true,
  dismissible = true,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  /** The line above the title — the record's context, not a subtitle. */
  kicker?: ReactNode;
  /** Design width in px; below 640 the dialog is full screen regardless. */
  width?: number;
  children: ReactNode;
  /** The action row. Sticky, so Guardar never scrolls out of reach. */
  footer?: ReactNode;
  /**
   * False where a stray click beside the dialog must not throw away what is
   * typed — an email being written. Cerrar, Cancelar and Escape still close it.
   */
  closeOnBackdrop?: boolean;
  /**
   * False for a question that has to be answered: no ✕, and neither Escape nor
   * the backdrop closes it. Only the buttons in the dialog itself do, and
   * `onClose` is never called.
   */
  dismissible?: boolean;
}) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;

    if (open) {
      // `showModal` is absent in some test environments; the `open` attribute
      // renders the same tree non-modally, which is all an assertion needs.
      if (typeof dialog.showModal === "function") {
        if (!dialog.open) dialog.showModal();
      } else {
        dialog.setAttribute("open", "");
      }
      // `showModal()` leaves focus on the dialog itself unless something
      // inside claims it, so the seller opens a form and has to click before
      // typing. The platform's answer is the `autofocus` attribute — which
      // React never renders: it special-cases `autoFocus` into a `focus()`
      // call at mount, and at mount this element is still `display:none`, so
      // the call does nothing and the browser has no attribute to find.
      // `data-autofocus` passes through untouched, and this honours it.
      dialog.querySelector<HTMLElement>("[data-autofocus]")?.focus();
    } else if (dialog.open) {
      if (typeof dialog.close === "function") dialog.close();
      else dialog.removeAttribute("open");
    }
  }, [open]);

  // Scroll lock. `showModal` blocks scrolling of the document in most engines
  // but not on iOS Safari, where the page behind keeps moving under the modal.
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  return (
    <dialog
      ref={ref}
      className="dc"
      style={{ width }}
      aria-labelledby={titleId}
      // Escape fires `cancel` and then closes the element itself. Without
      // telling React, `open` stays true and the dialog can never be reopened —
      // the classic "the button stopped working" bug.
      onCancel={(event) => {
        event.preventDefault();
        if (dismissible) onClose();
      }}
      // Deliberately NOT wired to the element's own `close` event. That event
      // fires as a consequence of the effect above calling `dialog.close()`,
      // so forwarding it calls `onClose` a second time for every close — once
      // from whatever the user clicked and once from our own reaction to it.
      // Harmless for a `setOpen(false)`, not harmless for an `onClose` that
      // resets a form or navigates. `cancel` already covers Escape, which is
      // the only close the browser starts on its own.
      // A click that lands on the element itself rather than on its content is
      // a click on the backdrop: the padding is zero, so there is no rim.
      onClick={(event) => {
        if (dismissible && closeOnBackdrop && event.target === ref.current) onClose();
      }}
    >
      <div className="flex items-start justify-between gap-5 border-b-2 border-rule px-5 py-5 sm:px-7">
        <div>
          {/* A paragraph, not a heading — see the note in `ui.tsx`. */}
          {kicker ? <p className="kicker mb-1.5 text-brand-700">{kicker}</p> : null}
          <h3 id={titleId} className="text-[21px] sm:text-[25px]">
            {title}
          </h3>
        </div>
        {dismissible ? (
          <button
            type="button"
            className="btn btn-ghost flex h-8 w-8 shrink-0 items-center justify-center p-0"
            aria-label={t("common.close")}
            onClick={onClose}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        ) : null}
      </div>

      <div className="px-5 py-5 sm:px-7">{children}</div>

      {footer ? (
        <div className="dc-footer flex flex-wrap items-center gap-2.5 px-5 py-4 sm:px-7">
          {footer}
        </div>
      ) : null}
    </dialog>
  );
}
